import { useState, useEffect } from 'react';

export interface CompanySettings {
  id?: string;
  companyName: string;
  logo?: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  taxId?: string;
  defaultCurrency: string;
  defaultTaxRate?: number;
  defaultPaymentTerms?: number; // days
  paymentInstructions?: string;
  brandColor?: string;
}

const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  companyName: 'Your Company',
  address: {},
  defaultCurrency: 'USD',
  defaultPaymentTerms: 30,
};

export const useCompanySettings = () => {
  const [settings, setSettings] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to fetch from API first
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE}/api/company/profile`);

        if (response.ok) {
          const responseData = await response.json();
          const data = responseData.data; // API returns { data: companyProfile }
          
          // Convert logo URL to base64 if it exists
          let logoBase64 = undefined;
          if (data.logoUrl) {
            try {
              const logoUrl = `${API_BASE}${data.logoUrl}`;
              const response = await fetch(logoUrl);
              const blob = await response.blob();
              const base64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              logoBase64 = base64 as string;
            } catch (error) {
              console.error('Failed to convert logo to base64:', error);
            }
          }

          // Transform API response to our CompanySettings format
          const companySettings: CompanySettings = {
            id: data.id?.toString(),
            companyName: data.name || 'Your Company',
            logo: logoBase64 || (data.logoUrl ? `${API_BASE}${data.logoUrl}` : undefined),
            address: {
              street: data.addressLine1,
              city: data.city,
              state: data.state,
              zipCode: data.postalCode,
              country: data.country
            },
            phone: data.phone,
            email: data.email,
            website: data.website,
            defaultCurrency: data.currency || 'USD',
            defaultTaxRate: 0, // Not available in CompanyProfile model
            defaultPaymentTerms: data.paymentTerms || 30,
            paymentInstructions: '', // Not available in CompanyProfile model
            brandColor: data.primaryColor || '#edff00'
          };

          console.log('Company settings loaded:', {
            logoUrl: data.logoUrl,
            fullLogoUrl: companySettings.logo,
            logoType: logoBase64 ? 'base64' : 'url',
            companyName: companySettings.companyName
          });

          setSettings(companySettings);
        } else {
          // Fallback to mock data if API fails
          console.warn('Failed to fetch company settings, using mock data');
          const mockSettings: CompanySettings = {
            id: '1',
            companyName: 'Your Company',
            logo: undefined,
            address: {
              street: '',
              city: '',
              state: '',
              zipCode: '',
              country: ''
            },
            phone: '',
            email: '',
            website: '',
            defaultCurrency: 'USD',
            defaultTaxRate: 0,
            defaultPaymentTerms: 30,
            paymentInstructions: '',
            brandColor: '#edff00'
          };
          setSettings(mockSettings);
        }
      } catch (err) {
        console.error('Failed to fetch company settings:', err);
        setError('Failed to load company settings');
        
        // Keep default settings on error
        const fallbackSettings: CompanySettings = {
          id: '1',
          companyName: 'Your Company',
          logo: undefined,
          address: {},
          phone: '',
          email: '',
          website: '',
          defaultCurrency: 'USD',
          defaultTaxRate: 0,
          defaultPaymentTerms: 30,
          paymentInstructions: '',
          brandColor: '#96ff01'
        };
        setSettings(fallbackSettings);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanySettings();
  }, []);

  const isComplete = () => {
    return !!(
      settings.companyName &&
      settings.email &&
      settings.address.street &&
      settings.address.city &&
      settings.address.state &&
      settings.address.zipCode &&
      settings.address.country
    );
  };

  const getFormattedAddress = () => {
    const { street, city, state, zipCode, country } = settings.address;
    const parts = [];
    
    if (street) parts.push(street);
    if (city && state) parts.push(`${city}, ${state} ${zipCode || ''}`.trim());
    else if (city) parts.push(city);
    if (country && country !== 'USA') parts.push(country);
    
    return parts.join('\n');
  };

  return {
    settings,
    loading,
    error,
    isComplete: isComplete(),
    formattedAddress: getFormattedAddress(),
    refreshSettings: () => {
      // Re-fetch settings if needed
      setLoading(true);
      // fetchCompanySettings(); // Would call the fetch function again
    }
  };
};
